import React, { useState } from 'react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Activity,
  Building2,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  FileText,
  Mail,
  User,
  Stethoscope,
} from 'lucide-react';
import RegistrationFormStep from './registration/RegistrationFormStep';
import PersonalRegistrationDialog from './PersonalRegistrationDialog';
import ProfessionalRegistrationDialog from './ProfessionalRegistrationDialog';

const STEPS = {
  PROFILE: 0,
  FORM: 1,
  TERMS: 2,
  SUCCESS: 3,
};

const RegistrationModal = ({ open, onOpenChange, initialProfile = null }) => {
  const [step, setStep] = useState(initialProfile ? STEPS.FORM : STEPS.PROFILE);
  const [profileType, setProfileType] = useState(initialProfile);
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [personalDialogOpen, setPersonalDialogOpen] = useState(false);
  const [professionalDialogOpen, setProfessionalDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    org_name: '',
    cnpj: '',
    razao_social: '',
    cep: '',
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    state: '',
    phone: '',
    org_email: '',
    rh_name: '',
    rh_phone: '',
    rh_email: '',
    accepted_plans: ['basic', 'plus', 'premium', 'gold', 'platinum', 'action', 'personal_plus', 'personal_gold', 'personal_premium', 'personal_platinum'],
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetModal = () => {
    setStep(STEPS.PROFILE);
    setProfileType(null);
    setTermsAccepted(false);
    setShowPassword(false);
    setFormData({
      name: '', email: '', password: '', org_name: '', cnpj: '',
      razao_social: '', cep: '', street: '', number: '', neighborhood: '',
      city: '', state: '', phone: '', org_email: '', rh_name: '',
      rh_phone: '', rh_email: '',
      accepted_plans: ['basic', 'plus', 'premium', 'gold', 'platinum', 'action', 'personal_plus', 'personal_gold', 'personal_premium', 'personal_platinum'],
    });
  };

  const handleClose = (val) => {
    if (!val) resetModal();
    onOpenChange(val);
  };

  const selectProfile = (type) => {
    setProfileType(type);
    setStep(STEPS.FORM);
  };

  const openPersonalFlow = () => {
    onOpenChange(false);
    setTimeout(() => setPersonalDialogOpen(true), 150);
  };

  const openProfessionalFlow = () => {
    onOpenChange(false);
    setTimeout(() => setProfessionalDialogOpen(true), 150);
  };

  const handleFormNext = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password || !formData.org_name) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    if (profileType === 'company' && !formData.cnpj) {
      toast.error('CNPJ é obrigatório para empresas');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    setStep(STEPS.TERMS);
  };

  const handleSubmit = async () => {
    if (!termsAccepted) {
      toast.error('Aceite os termos de uso para continuar');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        profile_type: profileType,
        name: formData.name,
        email: formData.email,
        password: formData.password,
        org_name: formData.org_name,
        cnpj: formData.cnpj || null,
        razao_social: formData.razao_social || null,
        cep: formData.cep || null,
        street: formData.street || null,
        number: formData.number || null,
        neighborhood: formData.neighborhood || null,
        city: formData.city || null,
        state: formData.state || null,
        phone: formData.phone || null,
        org_email: formData.org_email || null,
        rh_name: formData.rh_name || null,
        rh_phone: formData.rh_phone || null,
        rh_email: formData.rh_email || null,
        accepted_plans: profileType === 'gym' ? formData.accepted_plans : null,
      };

      await axios.post(`${API}/auth/register-organization`, payload);
      // Show success message step - DO NOT auto-login
      setStep(STEPS.SUCCESS);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const isCompany = profileType === 'company';
  const orgLabel = isCompany ? 'Empresa' : 'Academia';

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={`${step === STEPS.PROFILE ? 'sm:max-w-4xl' : 'sm:max-w-lg'} p-0`} style={{ maxHeight: '90dvh', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }} data-testid="registration-modal">
        {/* Progress bar */}
        <div className="flex gap-1 px-6 pt-5">
          {[0, 1, 2, 3].map(i => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${
                i <= step ? 'bg-slate-900' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>

        <div className="px-6 pb-6">
          {/* STEP 0: Profile Selection */}
          {step === STEPS.PROFILE && (
            <>
              <DialogHeader className="pt-2 pb-4">
                <DialogTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  <Activity className="w-5 h-5 text-blue-600" />
                  Criar Conta
                </DialogTitle>
                <DialogDescription>
                  Selecione o tipo de conta que deseja criar
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
                <button
                  onClick={() => selectProfile('company')}
                  data-testid="select-company-profile"
                  className="group bg-white border-2 border-slate-200 rounded-xl p-4 text-left transition-all hover:border-blue-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <div className="w-11 h-11 bg-blue-600 rounded-xl flex items-center justify-center mb-3 transition-transform group-hover:scale-110">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-900 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Empresa Parceira
                  </h3>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Gerencie a saúde corporativa dos colaboradores
                  </p>
                </button>

                <button
                  onClick={() => selectProfile('gym')}
                  data-testid="select-gym-profile"
                  className="group bg-white border-2 border-slate-200 rounded-xl p-4 text-left transition-all hover:border-orange-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <div className="w-11 h-11 bg-orange-500 rounded-xl flex items-center justify-center mb-3 transition-transform group-hover:scale-110">
                    <Activity className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-900 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Academia Parceira
                  </h3>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Cadastre alunos e registre avaliações físicas
                  </p>
                </button>

                <button
                  onClick={openProfessionalFlow}
                  data-testid="select-professional-profile"
                  className="group bg-white border-2 border-slate-200 rounded-xl p-4 text-left transition-all hover:border-teal-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <div className="w-11 h-11 bg-teal-600 rounded-xl flex items-center justify-center mb-3 transition-transform group-hover:scale-110">
                    <Stethoscope className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-900 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Profissional de Saúde
                  </h3>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Educação Física, Fisio, Nutri, Estética e mais
                  </p>
                </button>

                <button
                  onClick={openPersonalFlow}
                  data-testid="select-personal-profile"
                  className="group bg-white border-2 border-slate-200 rounded-xl p-4 text-left transition-all hover:border-emerald-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <div className="w-11 h-11 bg-emerald-600 rounded-xl flex items-center justify-center mb-3 transition-transform group-hover:scale-110">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-900 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Pessoa Física
                  </h3>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Cadastre-se como aluno individual e acesse a rede
                  </p>
                </button>
              </div>
            </>
          )}

          {/* STEP 1: Registration Form */}
          {step === STEPS.FORM && (
            <RegistrationFormStep
              form={formData}
              setForm={setFormData}
              isCompany={isCompany}
              orgLabel={orgLabel}
              STEPS={STEPS}
              setStep={setStep}
              setProfileType={setProfileType}
              handleFormNext={handleFormNext}
              loading={loading}
              cepLoading={cepLoading}
              setCepLoading={setCepLoading}
            />
          )}

          {/* STEP 2: Terms of Use */}
          {step === STEPS.TERMS && (
            <>
              <DialogHeader className="pt-2 pb-3">
                <DialogTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  <FileText className="w-5 h-5 text-blue-600" />
                  Termos de Uso
                </DialogTitle>
                <DialogDescription>Leia e aceite os termos para finalizar o cadastro</DialogDescription>
              </DialogHeader>

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 max-h-64 overflow-y-auto text-xs text-slate-600 leading-relaxed space-y-3">
                <h3 className="font-bold text-slate-900 text-sm">TERMO DE USO E CONDIÇÕES GERAIS DA PLATAFORMA VITAL PERFORMANCE</h3>

                <p><strong>1. ACEITAÇÃO DOS TERMOS</strong><br />
                Ao acessar, se cadastrar ou utilizar o sistema digital da Vital Performance, o usuário declara ter lido, compreendido e aceito integralmente os presentes Termos de Uso e Condições Gerais, bem como a Política de Privacidade aplicável. O uso da plataforma está condicionado à aceitação total destas condições.</p>

                <p><strong>2. CADASTRO E ACESSO À PLATAFORMA</strong><br />
                2.1. Para utilização dos serviços, o usuário deverá realizar cadastro com informações verdadeiras, completas e atualizadas.<br />
                2.2. O usuário é o único responsável pela veracidade dos dados fornecidos, bem como pela guarda e sigilo de suas credenciais de acesso.<br />
                2.3. É vedado: Criar contas com dados falsos ou de terceiros sem autorização; Compartilhar login e senha com terceiros; Utilizar a plataforma para fins ilícitos ou não autorizados.<br />
                2.4. A Vital Performance se reserva o direito de suspender ou cancelar contas que violem estes termos, sem aviso prévio.</p>

                <p><strong>3. REGRAS DE UTILIZAÇÃO</strong><br />
                3.1. O usuário compromete-se a utilizar a plataforma de forma ética, respeitando a legislação vigente e os princípios de boa-fé.<br />
                3.2. É proibido: Praticar qualquer tentativa de invasão, engenharia reversa ou exploração do sistema; Utilizar o sistema para prejudicar outros usuários ou parceiros; Inserir conteúdos ofensivos, ilegais ou que violem direitos de terceiros.</p>

                <p><strong>4. MODELO DE COBRANÇA E REGULAMENTAÇÃO DE VALORES</strong><br />
                4.1. A Vital Performance atua como plataforma intermediadora de serviços, podendo definir, estruturar e regulamentar os modelos de cobrança aplicáveis.<br />
                4.2. Os valores cobrados na plataforma poderão incluir: Assinaturas mensais; Taxas de uso; Comissões sobre transações; Planos recorrentes ou personalizados.<br />
                4.3. A Vital Performance se reserva o direito de: Alterar valores, planos e modelos de monetização a qualquer momento; Criar campanhas promocionais com regras específicas; Definir critérios de elegibilidade para determinados serviços.<br />
                4.4. Todas as alterações serão comunicadas previamente dentro da plataforma ou por canais oficiais.</p>

                <p><strong>5. PAGAMENTOS E RESPONSABILIDADES FINANCEIRAS</strong><br />
                5.1. O usuário concorda em cumprir integralmente com as obrigações financeiras assumidas no momento da contratação.<br />
                5.2. Em caso de inadimplência, a plataforma poderá: Suspender o acesso; Cancelar serviços ativos; Aplicar encargos conforme legislação vigente.</p>

                <p><strong>6. PRIVACIDADE E PROTEÇÃO DE DADOS</strong><br />
                6.1. A Vital Performance compromete-se com a proteção dos dados pessoais dos usuários, em conformidade com a Lei Geral de Proteção de Dados (LGPD).<br />
                6.2. Os dados coletados são utilizados para: Operação da plataforma; Personalização da experiência do usuário; Comunicação e suporte; Cumprimento de obrigações legais.<br />
                6.3. A plataforma adota medidas técnicas e administrativas para garantir a segurança das informações.<br />
                6.4. O usuário tem direito a: Acessar seus dados; Corrigir informações; Solicitar exclusão, quando aplicável.</p>

                <p><strong>7. PROPRIEDADE INTELECTUAL</strong><br />
                7.1. Todo o conteúdo, tecnologia, design e funcionalidades da plataforma são de propriedade exclusiva da Vital Performance.<br />
                7.2. É proibida a reprodução, cópia ou uso indevido sem autorização expressa.</p>

                <p><strong>8. LIMITAÇÃO DE RESPONSABILIDADE</strong><br />
                8.1. A Vital Performance não se responsabiliza por: Falhas decorrentes de uso inadequado da plataforma; Problemas técnicos externos; Interrupções não previstas por fatores fora do seu controle.</p>

                <p><strong>9. ALTERAÇÕES DOS TERMOS</strong><br />
                9.1. A Vital Performance poderá atualizar estes Termos a qualquer momento, visando melhoria contínua do serviço.<br />
                9.2. O uso contínuo da plataforma após alterações implica concordância com os novos termos.</p>

                <p><strong>10. DISPOSIÇÕES GERAIS</strong><br />
                10.1. Estes Termos constituem o acordo integral entre as partes.<br />
                10.2. Fica eleito o foro da comarca do domicílio da empresa para dirimir quaisquer conflitos oriundos destes termos.</p>

                <p className="text-center font-semibold text-slate-800 pt-2 border-t border-slate-200">
                  SAULO PAIXÃO FERNANDES<br />
                  <span className="font-normal text-slate-500">CEO VITAL PERFORMANCE</span>
                </p>
              </div>

              <div className="flex items-start gap-3 mt-4 p-3 bg-blue-50 rounded-xl">
                <Checkbox
                  id="reg-terms-checkbox"
                  data-testid="reg-terms-checkbox"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                />
                <label htmlFor="reg-terms-checkbox" className="text-sm text-slate-700 cursor-pointer leading-relaxed">
                  Declaro que li e aceito os <strong>Termos de Uso dos Serviços</strong> e a <strong>Política de Preservação de Dados</strong>.
                </label>
              </div>

              <div className="flex gap-3 mt-4">
                <Button variant="outline" onClick={() => setStep(STEPS.FORM)} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!termsAccepted || loading}
                  data-testid="reg-submit-btn"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Criar Conta e Escolher Plano
                </Button>
              </div>
            </>
          )}

          {/* STEP 3: Success Message */}
          {step === STEPS.SUCCESS && (
            <>
              <div className="pt-6 pb-2 text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                  </div>
                </div>
                <h2
                  className="text-lg font-black text-slate-900 leading-tight mb-4 px-2"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                  data-testid="registration-success-title"
                >
                  PARABÉNS PELA ESCOLHA, EM BREVE NOSSO ADMINISTRADOR ENTRARA EM CONTATO, AGORA VERIFIQUE SEU EMAIL E FACA LOGIN
                </h2>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 justify-center mb-2">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-semibold text-blue-800">Verifique seu e-mail</span>
                  </div>
                  <p className="text-xs text-blue-700">
                    Enviamos um link de verificação para <strong>{formData.email}</strong>. Clique no link para ativar sua conta e fazer login.
                  </p>
                </div>
                <Button
                  onClick={() => { handleClose(false); }}
                  data-testid="registration-success-close-btn"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                >
                  Entendido
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
    <PersonalRegistrationDialog
      open={personalDialogOpen}
      onClose={() => setPersonalDialogOpen(false)}
    />
    <ProfessionalRegistrationDialog
      open={professionalDialogOpen}
      onClose={() => setProfessionalDialogOpen(false)}
    />
    </>
  );
};

export default RegistrationModal;
